import { z } from "zod";
import { Id } from "../common/ids.js";
import { defineRoute } from "../http/route.js";
/**
 * The shop's customer-service rule (IM-4, card 045): who answers a buyer who contacts the shop. Agents are members
 * holding `inquiry:respond`; with no rule, or no such agent left, those who manage the shop answer.
 */
const Manager = { surface: "tenant", auth: "user", permission: "cs:manage" };
const Agents = z.array(Id).min(1).max(50).describe("Memberships, in turn order");
export const CsRule = z
    .discriminatedUnion("mode", [
    z.object({ mode: z.literal("round_robin"), agents: Agents }),
    z.object({
        mode: z.literal("by_category"),
        categories: z
            .array(z.object({ categoryId: Id, agents: Agents }))
            .min(1)
            .max(100)
            .describe("A product's leaf, or the nearest category above it with a rule, picks the agents"),
        agents: z
            .array(Id)
            .max(50)
            .describe("For products no category rule covers; empty for the shop's managers"),
    }),
    z.object({ mode: z.literal("fixed"), agent: Id }),
])
    .meta({ id: "CsRule" });
export const CsRouting = z
    .object({
    rule: CsRule.nullable().describe("Null: those who manage the shop answer"),
    eligibleAgents: z
        .array(z.object({ membershipId: Id, userId: Id, displayName: z.string() }))
        .describe("Members who may answer now: they hold `inquiry:respond`"),
    version: z.number().int().nonnegative(),
})
    .meta({ id: "CsRouting" });
export const getCsRouting = defineRoute({
    ...Manager,
    id: "catalog.cs_routing.get",
    method: "GET",
    path: "/v1/shop/cs-routing",
    marks: ["tenant"],
    summary: "The customer-service rule and the members it may name",
    prd: ["IM-4"],
    responses: { 200: { description: "The rule", schema: CsRouting } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const putCsRouting = defineRoute({
    ...Manager,
    id: "catalog.cs_routing.put",
    method: "PUT",
    path: "/v1/shop/cs-routing",
    marks: ["tenant", "if-match"],
    summary: "Set the rule: round robin among agents, by category, or one fixed agent. Every agent must hold `inquiry:respond`; If-Match is 0 for the first rule",
    prd: ["IM-4"],
    request: { body: CsRule },
    responses: { 200: { description: "The rule", schema: CsRouting } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "VERSION_CONFLICT",
    ],
});
export const csRoutingRoutes = [getCsRouting, putCsRouting];
//# sourceMappingURL=cs-routing.routes.js.map